import { useState } from 'react';
import { Link } from 'react-router-dom';
import Logo from '../../components/Logo';
import { Button, Input } from '../../components/Ui';
import api from '../../api/endpoints';
import { colors } from '../../theme';

/**
 * Deleting an account, at /delete-account.
 *
 * Google Play requires a page where somebody can delete their account without
 * installing the app, so this sits outside the sign-in guards. It proves the
 * number belongs to whoever is asking by sending a code to it first -
 * otherwise anyone could delete anyone by typing a phone number.
 *
 * It tells them plainly what goes and what stays before they confirm, because
 * "delete" that quietly keeps things is the thing the policy exists to stop.
 */

const GOES = [
  'Your account, name and mobile number',
  'Saved addresses',
  'Your basket and saved items',
  'Reviews and ratings you wrote',
  'Support conversations',
  'Anything you listed for sale - vehicles, property, animals',
  'Your work profile and job applications',
  'Special day messages you scheduled',
];

const STAYS = [
  'Orders, bookings, rides and payments - with your name, number and address wiped off them',
];

const Panel = ({ children, tone }) => (
  <div style={{
    background: colors.card,
    border: `1px solid ${tone === 'warn' ? '#F5C2A0' : colors.line}`,
    borderRadius: 16,
    padding: '18px 20px',
    marginBottom: 18,
  }}
  >
    {children}
  </div>
);

const DeleteAccountPage = () => {
  const [step, setStep] = useState('phone');   // phone -> code -> done
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');
  const [note, setNote] = useState('');
  const [business, setBusiness] = useState(null);
  const [loading, setLoading] = useState(false);

  const start = async (e) => {
    if (e) e.preventDefault();
    const clean = phone.replace(/\D/g, '').slice(-10);
    if (clean.length !== 10) { setError('Enter a valid 10 digit mobile number'); return; }
    setError('');
    setLoading(true);
    try {
      const res = await api.auth.startDeletion({ phone: clean });
      setPhone(clean);
      setNote(res.message || '');
      setStep('code');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const confirm = async (e) => {
    if (e) e.preventDefault();
    if (!otp.trim()) { setError('Enter the code we sent you'); return; }
    setError('');
    setLoading(true);
    try {
      const res = await api.auth.confirmDeletion({ phone, otp: otp.trim() });
      setBusiness(res.data && res.data.business);
      setStep('done');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ background: colors.bg, minHeight: '100vh' }}>
      <header style={{
        borderBottom: `1px solid ${colors.line}`, background: colors.card, padding: '14px 20px',
      }}
      >
        <div style={{ maxWidth: 680, margin: '0 auto' }}>
          <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none' }}>
            <Logo size={34} />
            <span style={{ fontWeight: 900, color: colors.ink, fontSize: 16 }}>KAAMWALA</span>
          </Link>
        </div>
      </header>

      <main style={{ maxWidth: 680, margin: '0 auto', padding: '36px 20px 80px' }}>
        <h1 style={{ fontSize: 28, fontWeight: 900, color: colors.ink, marginBottom: 8 }}>
          Delete your account
        </h1>
        <p style={{ color: colors.muted, fontSize: 15, lineHeight: 1.7, marginBottom: 26 }}>
          You can do this yourself, here, without the app. We send a code to your mobile number to
          check it is really you, and then everything below is deleted the same day.
        </p>

        {step !== 'done' ? (
          <>
            <Panel>
              <div style={{ fontWeight: 800, color: colors.ink, marginBottom: 10 }}>
                What gets deleted
              </div>
              <ul style={{
                paddingLeft: 20, display: 'grid', gap: 7, color: colors.muted, fontSize: 14.5, lineHeight: 1.6,
              }}
              >
                {GOES.map((line) => <li key={line}>{line}</li>)}
              </ul>
            </Panel>

            <Panel tone="warn">
              <div style={{ fontWeight: 800, color: colors.ink, marginBottom: 10 }}>
                What we have to keep
              </div>
              <ul style={{
                paddingLeft: 20, display: 'grid', gap: 7, color: colors.muted, fontSize: 14.5, lineHeight: 1.6,
              }}
              >
                {STAYS.map((line) => <li key={line}>{line}</li>)}
              </ul>
              <p style={{ color: colors.muted, fontSize: 13.5, marginTop: 10, lineHeight: 1.6 }}>
                A shop has to account for what it sold and a tax return has to add up. Those rows
                stay, but with every personal field overwritten - they record that a sale
                happened, not who made it.
              </p>
            </Panel>
          </>
        ) : null}

        {step === 'phone' ? (
          <form onSubmit={start}>
            <Input
              label="Your mobile number"
              value={phone}
              onChange={(e) => { setPhone(e.target.value); setError(''); }}
              placeholder="10 digit number"
              inputMode="numeric"
              maxLength={10}
              error={error}
            />
            <Button
              title={loading ? 'Sending…' : 'Send me the code'}
              onClick={start}
              loading={loading}
              full
              style={{ marginTop: 16 }}
            />
            <p style={{ color: colors.muted, fontSize: 13, marginTop: 14, lineHeight: 1.6 }}>
              Changed your mind? Nothing is deleted until you enter the code.
            </p>
          </form>
        ) : null}

        {step === 'code' ? (
          <form onSubmit={confirm}>
            <p style={{ color: colors.muted, fontSize: 14.5, marginBottom: 14, lineHeight: 1.7 }}>
              {note || `If ${phone} has a KAAMWALA account, we have sent it a code.`}
            </p>
            <Input
              label="The code"
              value={otp}
              onChange={(e) => { setOtp(e.target.value); setError(''); }}
              placeholder="6 digit code"
              inputMode="numeric"
              maxLength={6}
              error={error}
            />
            <Button
              title={loading ? 'Deleting…' : 'Delete my account permanently'}
              onClick={confirm}
              loading={loading}
              full
              style={{ marginTop: 16, background: colors.danger }}
            />
            <Button
              title="Go back"
              variant="light"
              onClick={() => { setStep('phone'); setOtp(''); setError(''); }}
              full
              style={{ marginTop: 10 }}
            />
            <p style={{ color: colors.muted, fontSize: 13, marginTop: 14, lineHeight: 1.6 }}>
              This cannot be undone. There is no waiting period to change your mind in.
            </p>
          </form>
        ) : null}

        {step === 'done' ? (
          <Panel>
            <div style={{ fontSize: 34, marginBottom: 8 }}>✓</div>
            <div style={{ fontWeight: 800, color: colors.ink, fontSize: 18, marginBottom: 8 }}>
              Your account has been deleted
            </div>
            <p style={{ color: colors.muted, fontSize: 14.5, lineHeight: 1.7 }}>
              Everything personal on
              {' '}
              {phone}
              {' '}
              is gone. You can sign up again any time with the same number - it will start empty.
            </p>
            {business ? (
              <p style={{ color: colors.muted, fontSize: 14.5, lineHeight: 1.7, marginTop: 12 }}>
                Your business
                {' '}
                <b>{business}</b>
                {' '}
                has been closed and removed from the app. Its past orders stay with the
                marketplace records, without your details on them. If that was a mistake, write to
                {' '}
                <a href="mailto:support@kaamwala.com" style={{ color: colors.primary, fontWeight: 700 }}>
                  support@kaamwala.com
                </a>
                .
              </p>
            ) : null}
            <Link
              to="/"
              style={{
                display: 'inline-block', marginTop: 18, color: colors.primary, fontWeight: 700,
              }}
            >
              Back to KAAMWALA
            </Link>
          </Panel>
        ) : null}

        <p style={{ color: colors.muted, fontSize: 13, marginTop: 30, lineHeight: 1.7 }}>
          Trouble with this?
          {' '}
          <a href="mailto:support@kaamwala.com" style={{ color: colors.primary, fontWeight: 700 }}>
            support@kaamwala.com
          </a>
          {' · '}
          <Link to="/privacy" style={{ color: colors.primary, fontWeight: 700 }}>Privacy policy</Link>
        </p>
      </main>
    </div>
  );
};

export default DeleteAccountPage;
